/**
 * Comprehensive Bot Detection System for Shopify
 * Prevents bot/crawler errors from polluting Sentry reports
 */

class BotDetector {
  constructor() {
    this.isBot = false;
    this.botReasons = [];
    this.detectionComplete = false;
    
    // Initialize detection immediately
    this.detectBot();
    
    // Make detection results globally available
    window.SentryBotDetector = this;
  }

  /**
   * Main bot detection logic
   */
  detectBot() {
    try {
      // Device-based detection
      this.checkDeviceSignatures();
      
      // User agent analysis
      this.checkUserAgent();
      
      // Browser capability testing
      this.checkBrowserCapabilities();
      
      // DOM availability checks
      this.checkDOMAvailability();
      
      // WebKit compatibility
      this.checkWebKitCompatibility();
      
      // Network/execution environment
      this.checkExecutionEnvironment();
      
      this.detectionComplete = true;
      
      if (this.isBot && this.isDevelopment()) {
        console.warn('[Bot Detector] Bot detected:', this.botReasons);
      }
    } catch (error) {
      // If detection fails, err on the side of caution for real users
      this.detectionComplete = true;
      if (this.isDevelopment()) {
        console.warn('[Bot Detector] Detection failed:', error);
      }
    }
  }

  /**
   * Check for known bot device signatures
   */
  checkDeviceSignatures() {
    const ua = navigator.userAgent || '';
    
    // Generic Android devices with suspicious patterns
    if (ua.includes('Generic_Android') || ua.includes('Android.*Generic')) {
      this.markAsBot('generic_android_device');
    }
    
    // Check for device model "K" which is common in bots
    const deviceMatch = ua.match(/;\s*([^;)]+)\s*(?:Build|;|\))/);
    if (deviceMatch && deviceMatch[1].trim() === 'K') {
      this.markAsBot('suspicious_device_model_k');
    }
    
    // Facebook app browser patterns that indicate crawlers
    if (ua.includes('FBAN') || ua.includes('FBAV')) {
      // Additional checks for Facebook crawlers vs real app
      if (!ua.includes('Mobile') || ua.includes('FacebookExternalHit')) {
        this.markAsBot('facebook_crawler');
      }
    }
    
    // Known bot user agents
    const botPatterns = [
      /bot|crawler|spider|scraper/i,
      /facebookexternalhit/i,
      /twitterbot/i,
      /linkedinbot/i,
      /whatsapp/i,
      /telegrambot/i
    ];
    
    for (const pattern of botPatterns) {
      if (pattern.test(ua)) {
        this.markAsBot('known_bot_user_agent');
        break;
      }
    }
  }

  /**
   * Analyze user agent for suspicious patterns
   */
  checkUserAgent() {
    const ua = navigator.userAgent || '';
    
    // Empty or minimal user agent
    if (!ua || ua.length < 10) {
      this.markAsBot('minimal_user_agent');
    }
    
    // Suspicious browser version patterns
    if (ua.includes('Chrome/0.0.0') || ua.includes('Safari/0.0.0')) {
      this.markAsBot('zero_version_browser');
    }
    
    // Headless browser indicators
    if (ua.includes('HeadlessChrome') || ua.includes('PhantomJS')) {
      this.markAsBot('headless_browser');
    }
  }

  /**
   * Test browser capabilities that bots often lack
   */
  checkBrowserCapabilities() {
    // Check for missing standard APIs that real browsers have
    if (typeof window === 'undefined') {
      this.markAsBot('no_window_object');
      return;
    }
    
    // Bots often don't implement localStorage properly
    try {
      if (!window.localStorage) {
        this.markAsBot('no_localstorage');
      } else {
        // Test if localStorage actually works
        const testKey = '__bot_test__';
        localStorage.setItem(testKey, 'test');
        const retrieved = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        
        if (retrieved !== 'test') {
          this.markAsBot('broken_localstorage');
        }
      }
    } catch (e) {
      // Some privacy browsers block localStorage, but this shouldn't mark as bot
      // Only mark as bot if the error indicates missing implementation
      if (e.message && e.message.includes('not defined')) {
        this.markAsBot('localstorage_not_implemented');
      }
    }
    
    // Check for missing console (some bots strip this)
    if (!window.console || typeof window.console.log !== 'function') {
      this.markAsBot('no_console');
    }
    
    // Check for missing standard browser globals
    if (!window.navigator || !window.document || !window.location) {
      this.markAsBot('missing_browser_globals');
    }
  }

  /**
   * Check DOM availability and behavior
   */
  checkDOMAvailability() {
    if (typeof document === 'undefined') {
      this.markAsBot('no_document');
      return;
    }
    
    // Only check for incomplete DOM if document is fully loaded
    // This prevents false positives during normal page load
    if (document.readyState === 'complete') {
      if (!document.body || !document.head) {
        this.markAsBot('incomplete_dom');
      }
    }
    
    // Check for document readiness
    if (document.readyState === 'loading' && !document.addEventListener) {
      this.markAsBot('no_event_listeners');
    }
    
    // Test basic DOM manipulation
    try {
      const testDiv = document.createElement('div');
      if (!testDiv || typeof testDiv.setAttribute !== 'function') {
        this.markAsBot('broken_dom_creation');
      }
    } catch (e) {
      this.markAsBot('dom_creation_failed');
    }
  }

  /**
   * Check WebKit specific issues that indicate crawlers
   */
  checkWebKitCompatibility() {
    // Check for webkit messageHandlers without proper implementation
    if (window.webkit && window.webkit.messageHandlers) {
      // Real iOS browsers have proper implementation
      // Bots often have partial/broken webkit objects
      try {
        const handlers = window.webkit.messageHandlers;
        if (typeof handlers !== 'object' || Object.keys(handlers).length === 0) {
          this.markAsBot('broken_webkit_handlers');
        }
      } catch (e) {
        this.markAsBot('webkit_handler_error');
      }
    }
  }

  /**
   * Check execution environment for bot indicators
   */
  checkExecutionEnvironment() {
    // Check for suspicious timing
    const startTime = Date.now();
    setTimeout(() => {
      const endTime = Date.now();
      // If setTimeout doesn't work or has suspicious timing
      if (endTime - startTime > 100) { // Should be ~0-10ms typically
        this.markAsBot('suspicious_timing');
      }
    }, 0);
    
    // Check for missing standard functions
    if (!window.setTimeout || !window.setInterval || !window.clearTimeout) {
      this.markAsBot('missing_timer_functions');
    }
    
    // Check for fetch API availability (most modern browsers have this)
    if (!window.fetch && !window.XMLHttpRequest) {
      this.markAsBot('no_http_capabilities');
    }
    
    // Screen and visual context checks
    if (screen.width === 0 || screen.height === 0) {
      this.markAsBot('zero_screen_dimensions');
    }
    
    if (window.outerWidth === 0 || window.outerHeight === 0) {
      this.markAsBot('zero_window_dimensions');
    }
  }

  /**
   * Mark this session as a bot with a specific reason
   */
  markAsBot(reason) {
    this.isBot = true;
    this.botReasons.push(reason);
  }

  /**
   * Check if we're in development environment
   */
  isDevelopment() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname.includes('.local') ||
           window.location.hostname.includes('127.0.0.1');
  }

  /**
   * Get bot status
   */
  isBotDetected() {
    return this.isBot;
  }

  /**
   * Get bot detection reasons
   */
  getBotReasons() {
    return this.botReasons;
  }

  /**
   * Check if detection is complete
   */
  isDetectionComplete() {
    return this.detectionComplete;
  }

  /**
   * Should this error be reported to Sentry?
   */
  shouldReportError(error, errorInfo = {}) {
    if (!this.detectionComplete || !this.isBot) {
      return true; // Report errors for real users or when detection incomplete
    }

    // Bot-specific error patterns that should be filtered
    const botErrorPatterns = [
      /webkit\.messageHandlers/i,
      /Failed to resolve module specifier/i,
      /Java object is gone/i,
      /SearchForm is not defined/i,
      /debounce is not defined/i,
      /Cannot read properties of null/i,
      /Cannot read properties of undefined/i,
      /Failed to fetch/i,
      /Load failed/i,
      /Access is denied/i,
      /The operation is insecure/i
    ];

    const errorMessage = error?.message || error?.toString() || '';
    const errorStack = error?.stack || '';
    const combinedError = `${errorMessage} ${errorStack}`;

    // Check if this is a bot-related error
    for (const pattern of botErrorPatterns) {
      if (pattern.test(combinedError)) {
        if (this.isDevelopment()) {
          console.log('[Bot Detector] Filtered bot error:', errorMessage, this.botReasons);
        }
        return false; // Don't report bot errors
      }
    }

    return true; // Report other errors even from detected bots
  }

  /**
   * Create a Sentry beforeSend function
   */
  createSentryBeforeSend(originalBeforeSend) {
    return (event, hint) => {
      // Always call original beforeSend first if it exists
      if (originalBeforeSend) {
        event = originalBeforeSend(event, hint);
        if (!event) return null;
      }

      // Don't filter if we haven't detected a bot
      if (!this.isBot) {
        return event;
      }

      // Check if this event should be filtered
      const error = hint?.originalException || hint?.syntheticException;
      if (!this.shouldReportError(error, event)) {
        return null; // Filter out bot errors
      }

      // Add bot detection context for debugging
      if (event.extra) {
        event.extra.botDetected = true;
        event.extra.botReasons = this.botReasons;
      } else {
        event.extra = {
          botDetected: true,
          botReasons: this.botReasons
        };
      }

      return event;
    };
  }
}

// Initialize bot detection immediately
new BotDetector();